import type { PropInstance } from "../document/types.ts";

/**
 * Depth sorting shared by props, characters and row-sorted tile layers (ADR-040): a sort row maps to a
 * fraction of one depth band so lower rows draw in front, and everything in a band interleaves by row.
 */

/**
 * The fraction of a depth band a sort row maps to, clamped so an offset never leaves the band.
 *
 * @param row Sort row in tiles (a prop's anchor row plus its offset, a character's feet row, a tile's row).
 * @param mapHeight Map height in tiles.
 * @returns A value in `[0, 1)`.
 */
export function sortFraction(row: number, mapHeight: number): number {
  const clamped = Math.min(mapHeight, Math.max(0, row + 1));
  return clamped / (mapHeight + 2);
}

/**
 * The rows beyond a map that are drawn beside it (ADR-147 decision 6): from `top` (at most 0) down to `bottom` (at
 * least the map's height), in the map's own rows. A neighbouring map placed north of the map has negative rows, one
 * placed south rows from the map's height on.
 */
export interface SortReach {
  readonly top: number;
  readonly bottom: number;
}

/**
 * The most Players band layers a map drawn beside another keeps in row order within one pixel by `reachSortFraction`, for
 * two maps of 256 rows of 32 pixel tiles; a page warns of a neighbour with more.
 */
export const REACH_BAND_LAYERS = 15;

/**
 * The fraction of a depth band a sort row maps to on a page that draws neighbouring maps beside its own (ADR-147
 * decision 6). A row of the map keeps exactly the fraction `sortFraction` gives it, so the map, its characters and the
 * depths its compile baked for its Behind and In front marks draw as they do alone. Rows north of the map share the
 * middle half of the gap under its row 0, and rows south of it the middle half of the gap over its last row, each in
 * row order, so a neighbour's props and row-sorted tiles interleave among themselves and every one of them draws behind
 * (north) or in front of (south) everything that stands on the map. The quarter left at each side of a gap keeps a
 * character's half `BAND_LAYER_STEP` and the band's layer steps from crossing it. One pixel of row beyond the map is
 * `0.5 / ((mapHeight + 2) * (span + 3) * tileSize)` of a band, where `span` is the rows on that side, which are taken
 * over one row more than they hold so that no fraction of the last of them is clamped: more than fifteen
 * band layers' steps for two maps of 256 rows of 32 pixel tiles.
 *
 * @param row Sort row in the map's tiles; negative north of it.
 * @param mapHeight The map's height in tiles.
 * @param reach The rows beyond the map that are drawn.
 * @returns A value in `[0, 1)`.
 * @example
 * reachSortFraction(-10, 256, { top: -256, bottom: 256 }); // under sortFraction(0, 256)
 */
export function reachSortFraction(row: number, mapHeight: number, reach: SortReach): number {
  if (row < 0 && reach.top < 0) {
    return (0.25 + 0.5 * sortFraction(row - reach.top, 1 - reach.top)) / (mapHeight + 2);
  }
  if (row >= mapHeight && reach.bottom > mapHeight) {
    return (mapHeight + 0.25 + 0.5 * sortFraction(row - mapHeight, reach.bottom - mapHeight + 1)) / (mapHeight + 2);
  }
  return sortFraction(row, mapHeight);
}

/**
 * How far apart layers in the Players band sit on one sort line (ADR-047, ADR-098 decision 5): a power of two,
 * so a layer's depth is exact in a double, and small enough that the whole range of the 256 layers a map may
 * hold, `256 * 2 ** -26`, is less than the depth of one pixel of sort line while
 * `(mapHeight + 2) * tileSize` is under `2 ** 18`, which is 8,190 rows of 32 pixel tiles. Up to that height a
 * line one pixel further south outranks every layer, a character one pixel south of a prop's line draws in
 * front of it on any band layer, and the layer order decides only between props on the same pixel line. An
 * animated cell's nudge is a twentieth of this step.
 */
export const BAND_LAYER_STEP = 2 ** -26;

/**
 * The depth one step of a prop's tie key adds (ADR-071 known difference 2, P1.6-T42, ADR-098 decision 5):
 * `BAND_LAYER_STEP / 2 ** 18`, a power of two, so a band under 256 plus any layer and key is exact in a
 * double (8 bits above the point, 44 below). The key of the last of 65,536 props stays under a quarter of
 * `BAND_LAYER_STEP`, so a key never reaches the next layer and a character's half step below the band still
 * puts it under every prop it ties with.
 */
export const TIE_STEP = 2 ** -44;

/**
 * A prop as a view draws it: the placed prop and, where the drawing side knows it, its tie key, which adds
 * `order * TIE_STEP` to its depth so equal depths fall in one order however the props reach the view. Without
 * a key a prop draws at its depth alone.
 */
export interface OrderedProp extends PropInstance {
  readonly order?: number;
}

/**
 * The tie key of each prop of one layer (ADR-071 known difference 2, P1.6-T42): its rank in the layer's draw
 * order, every prop not driven by presence first in document order, then every presence prop in document
 * order, so a roof wins its ties with the walls it stands on. The editor and the compile both key a layer's
 * whole prop list, behaviour props included, so the keys agree on both pages.
 *
 * @param props The layer's props in document order.
 * @param isPresence Whether a prop is driven by presence.
 * @returns The keys, one per prop in the same order: each a distinct whole number below `props.length`.
 */
export function tieKeys<T>(props: readonly T[], isPresence: (prop: T) => boolean): number[] {
  const presence = props.map(isPresence);
  const keys = new Array<number>(props.length);
  let next = 0;
  for (let i = 0; i < props.length; i += 1) {
    if (!presence[i]) {
      keys[i] = next;
      next += 1;
    }
  }
  for (let i = 0; i < props.length; i += 1) {
    if (presence[i]) {
      keys[i] = next;
      next += 1;
    }
  }
  return keys;
}

/**
 * A layer's props each carrying its tie key as `order` (P1.6-T42), for a view to draw by or a pick to break
 * ties by, as the map draws them. The props themselves are left as they are.
 *
 * @param props The layer's props in document order.
 * @param isPresence Whether a prop is driven by presence.
 * @returns Copies of the props in the same order, each with its key.
 */
export function withTieKeys(props: readonly PropInstance[], isPresence: (prop: PropInstance) => boolean): OrderedProp[] {
  const keys = tieKeys(props, isPresence);
  return props.map((prop, i) => ({ ...prop, order: keys[i] as number }));
}

/**
 * The depth a layer draws at (ADR-047): ground layers below the band stack at their index; layers in the
 * band share the band's depth, offset by their order so ties within a row resolve by layer, and add the
 * row fraction on top.
 *
 * @param index The layer's index in draw order.
 * @param band The Players band, the number of ground layers.
 * @returns The layer's base depth.
 */
export function layerDepth(index: number, band: number): number {
  return index < band ? index : band + (index - band) * BAND_LAYER_STEP;
}

/**
 * The order a map picture paints a map's layers in, which is the order the world draws them in (ADR-047): the layers
 * under the Players band stack first, and a terrain layer, which is flat art, draws whole at the band's floor, under
 * every prop and every row-sorted layer of the band, wherever it stands in the list. The rest follow in layer order.
 *
 * @param flat For each layer in layer order, whether it is a terrain layer.
 * @param band The Players band, the number of ground layers.
 * @returns The layer indices in the order to paint them.
 */
export function pictureLayerOrder(flat: readonly boolean[], band: number): number[] {
  const first: number[] = [];
  const rest: number[] = [];
  flat.forEach((isFlat, index) => (index < band || isFlat ? first : rest).push(index));
  return [...first, ...rest];
}

/**
 * The sort row ground frames take (ADR-040 amendment): half a row above the band's floor, so they draw over
 * every flat layer in the band (terrain and its per-layer step) and under everything that sorts by a real
 * row, whose smallest fraction is row 0.
 */
export const GROUND_ROW = -0.5;

/**
 * The sort row of a ground line at a world pixel (ADR-071 decision 2): a character whose feet line is the
 * line, or a prop whose art meets the ground there, sorts by it.
 *
 * @param pixelY The ground line in world pixels.
 * @param tileSize Tile size in pixels.
 * @returns The sort row, fractional.
 */
export function groundRow(pixelY: number, tileSize: number): number {
  return pixelY / tileSize - 1;
}

/**
 * The depth of a character whose feet line is `feetY` (ADR-071 decision 2): the band's depth less half a
 * `BAND_LAYER_STEP`, plus the row fraction of the feet line. The half step puts a character tied with a prop
 * under it on any band layer, and keeps it over the band's flat terrain and, below the map's first half
 * tile row, over ground frames. The game's characters and the editor's player layer take their depth here
 * alone.
 *
 * @param feetY The feet line in world pixels.
 * @param tileSize Tile size in pixels.
 * @param band The Players band, the number of ground layers.
 * @param mapHeight Map height in tiles.
 * @returns The depth.
 */
export function standingDepth(feetY: number, tileSize: number, band: number, mapHeight: number): number {
  return layerDepth(band, band) - BAND_LAYER_STEP / 2 + sortFraction(groundRow(feetY, tileSize), mapHeight);
}
